import React from 'react';
import { Sparkles, ArrowDown, ThumbsUp, ThumbsDown, PhoneOff } from 'lucide-react';

/**
 * CLOSE stage center panel.
 *
 * The final, UI-only stage of the cockpit (not a script.data.steps entry). After
 * the last real script step the caller lands here to either PITCH the Delta
 * Growth Workshop (when it's a fit) or politely DECLINE (when it's not).
 *
 * The recommendation is computed CLIENT-SIDE from the fit count — we never fire
 * the LLM copilot on this stage. Both blocks are ALWAYS visible; the verdict only
 * decides which is highlighted/expanded. The caller makes the final call.
 *
 * Fit count uses the SAME effective-tick logic as FitMatrix:
 *   manual fitSignals[signal] if set, else AI fitAi[signal].status === 'yes'.
 *
 * Verdict thresholds:
 *   - ICP script (is_icp === true):  strong >= 4/6, borderline 2-3, not a fit <= 1
 *   - generic   (is_icp === false): strong >= 5/6 (stricter), otherwise decline
 *
 * `fill` is the cockpit's display-time name substitution (fillPlaceholders bound
 * to prospectName/callerName) so [Name]/[your name] resolve consistently with the
 * rest of the cockpit. Stored script data is never mutated.
 */
function computeVerdict({ fitCount, total, isIcp }) {
  // "Strong fit" => recommend the pitch. Generic scripts demand a higher bar.
  const strongThreshold = isIcp ? 4 : 5;
  const isStrong = fitCount >= strongThreshold;

  let label;
  let tier;
  if (isIcp) {
    if (fitCount >= 4) {
      tier = 'strong';
      label = 'Strong fit';
    } else if (fitCount >= 2) {
      tier = 'borderline';
      label = 'Borderline';
    } else {
      tier = 'weak';
      label = 'Not a fit';
    }
  } else {
    tier = isStrong ? 'strong' : 'weak';
    label = isStrong ? 'Strong fit' : 'Not a strong fit';
  }

  return { isStrong, label, tier, strongThreshold };
}

/** One read-aloud beat, styled like ScriptPanel's spoken lines. */
function Beat({ text, accent = false, callout = null }) {
  if (!text) return null;
  return (
    <div
      className={`rounded-lg border p-3 ${
        accent ? 'border-[#00d4aa]/40 bg-[#00d4aa]/5' : 'border-white/10 bg-black/20'
      }`}
    >
      <p className="text-white/85 text-sm leading-relaxed">&ldquo;{text}&rdquo;</p>
      {callout && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#00d4aa]">
          <ArrowDown className="w-3.5 h-3.5" /> {callout}
        </div>
      )}
    </div>
  );
}

function ClosePanel({ workshopPitch, notFitClose, fitMatrix, fitSignals, fitAi, isIcp, fill }) {
  const signals = fitMatrix || [];
  const manual = fitSignals || {};
  const ai = fitAi || {};

  // Effective tick: manual override if set, else AI 'yes'. (Mirrors FitMatrix.)
  const isManual = (signal) => Object.prototype.hasOwnProperty.call(manual, signal);
  const effectiveTick = (signal) =>
    isManual(signal) ? manual[signal] === true : (ai[signal]?.status || 'unknown') === 'yes';

  const fitCount = signals.filter((s) => effectiveTick(s.signal)).length;
  const total = signals.length || 6;

  const { isStrong, label, tier } = computeVerdict({ fitCount, total, isIcp: isIcp === true });

  const f = typeof fill === 'function' ? fill : (t) => t;

  const pitch = workshopPitch || {};
  const arcs = Array.isArray(pitch.arcs) ? pitch.arcs : [];

  const verdictColor =
    tier === 'strong'
      ? 'text-[#00d4aa]'
      : tier === 'borderline'
      ? 'text-amber-300'
      : 'text-white/50';

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-[#00d4aa]" />
        <h2 className="text-lg font-semibold text-white">Close — Pitch or Decline</h2>
      </div>

      {/* Fit readout + verdict */}
      <div className="flex items-center gap-3 mb-3 text-sm">
        <span className="text-white/50">
          Fit: <span className="font-mono text-white/90">{fitCount}/{total}</span>
        </span>
        <span className={`font-semibold ${verdictColor}`}>{label}</span>
      </div>

      {/* Recommendation banner */}
      {isStrong ? (
        <div className="flex items-start gap-2 bg-[#00d4aa]/10 border border-[#00d4aa]/30 rounded-lg p-3 mb-4">
          <ThumbsUp className="w-4 h-4 text-[#00d4aa] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#00d4aa] leading-snug font-medium">
            They&rsquo;re a fit — pitch the Delta Growth Workshop.
          </p>
        </div>
      ) : (
        <div className="flex items-start gap-2 bg-white/5 border border-white/15 rounded-lg p-3 mb-4">
          <ThumbsDown className="w-4 h-4 text-white/50 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-white/70 leading-snug font-medium">
            Not a strong fit — use the polite decline.
          </p>
        </div>
      )}

      {/* PITCH block — always shown, expanded when recommended. */}
      <section
        className={`rounded-xl border mb-4 transition-all ${
          isStrong ? 'border-[#00d4aa]/40 bg-[#00d4aa]/[0.04]' : 'border-white/10 bg-black/10 opacity-70'
        }`}
      >
        <div className="flex items-center gap-2 px-4 pt-3 pb-2">
          <Sparkles className="w-4 h-4 text-[#00d4aa]" />
          <h3 className="text-sm font-semibold text-white">Pitch — Delta Growth Workshop ($995)</h3>
          {isStrong && (
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-[#00d4aa] bg-[#00d4aa]/10 border border-[#00d4aa]/20 rounded px-1.5 py-px">
              Recommended
            </span>
          )}
        </div>
        <div className="px-4 pb-4 space-y-2.5">
          <Beat text={f(pitch.transition)} accent={isStrong} />
          {arcs.map((arc, i) => (
            <Beat key={i} text={f(arc)} accent={isStrong} />
          ))}
          <Beat
            text={f(pitch.price_close)}
            accent={isStrong}
            callout="then stay silent — let them respond first"
          />
          <Beat text={f(pitch.booking)} accent={isStrong} />
          {!pitch.transition && !arcs.length && !pitch.price_close && !pitch.booking && (
            <p className="text-xs text-white/40">No workshop pitch defined for this script.</p>
          )}
        </div>
      </section>

      {/* DECLINE block — always shown, expanded when recommended. */}
      <section
        className={`rounded-xl border transition-all ${
          !isStrong ? 'border-white/25 bg-black/20' : 'border-white/10 bg-black/10 opacity-70'
        }`}
      >
        <div className="flex items-center gap-2 px-4 pt-3 pb-2">
          <PhoneOff className="w-4 h-4 text-white/60" />
          <h3 className="text-sm font-semibold text-white">Polite Decline</h3>
          {!isStrong && (
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-white/60 bg-white/10 border border-white/15 rounded px-1.5 py-px">
              Recommended
            </span>
          )}
        </div>
        <div className="px-4 pb-4">
          {notFitClose ? (
            <div className="rounded-lg border border-white/10 bg-black/20 p-3">
              <p className="text-white/85 text-sm leading-relaxed">&ldquo;{f(notFitClose)}&rdquo;</p>
            </div>
          ) : (
            <p className="text-xs text-white/40">No decline script defined for this script.</p>
          )}
        </div>
      </section>
    </div>
  );
}

export default ClosePanel;
